import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useModal } from '../../context/ModalContext';
import { changesApi } from '../../services/changesApi';
import type {
  ChangeCabDeck,
  ChangeCabDeckElement,
  ChangeCabDeckElementType,
  ChangeCabDeckSlide,
} from '../../types/changes';
import { getApiErrorMessage } from '../../utils/apiError';
import { fetchAuthenticatedObjectUrl } from '../../utils/fileDownload';
import { validateCabDeckImageFile } from '../../utils/uploadValidation';
import './cabPresentationTheme.css';
import { sanitizeCabDeckHtml } from './cabDeckRichText';
import { CabDeckSlideCanvas, elementKey } from './components/CabDeckSlideCanvas';
import CabDeckQuillEditor from './components/CabDeckQuillEditor';
import CabDeckSlideList from './components/CabDeckSlideList';
import CabDeckRiscosEditor from './components/CabDeckRiscosEditor';
import CabDeckConclusaoEditor from './components/CabDeckConclusaoEditor';
import CabDeckBrandEditor from './components/CabDeckBrandEditor';
import { loadCabFontScale } from './cabDeckTheme';
import { isChangeManager } from './changePermissions';
import {
  buildNewCabDeckSlide,
  CAB_NEW_SLIDE_OPTIONS,
  isBrandCabTemplate,
  isStructuredCabTemplate,
  resolveSlideLayout,
  rewriteLegacyCabBrandImagePath,
  type CabBrandLayout,
  type CabConclusaoLayout,
  type CabNewSlideKind,
  type CabRiscosLayout,
  type CabSlideLayout,
} from './cabPresentationTemplates';
import {
  elementLayerIndex,
  moveElementLayer,
  sortCabDeckElements,
  type CabDeckLayerDirection,
} from './cabDeckElementLayers';

type DragMode = 'move' | 'resize' | null;

function newTempId(): string {
  return `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

const DEFAULT_CROP = { xPct: 0, yPct: 0, wPct: 100, hPct: 100 };

const ChangeCabDeckEditorPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showConfirm } = useModal();
  const isGlobal = location.pathname.includes('/cab-deck/global');
  const changeId = Number(id);
  const [deck, setDeck] = useState<ChangeCabDeck | null>(null);
  const canManage = isGlobal
    ? isChangeManager(user)
    : Boolean(deck?.can_edit_cab_deck ?? isChangeManager(user));

  const [slides, setSlides] = useState<ChangeCabDeckSlide[]>([]);
  const [slideIndex, setSlideIndex] = useState(0);
  const [selectedKey, setSelectedKey] = useState<string | number | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [fieldPickerOpen, setFieldPickerOpen] = useState(false);
  const [newSlidePickerOpen, setNewSlidePickerOpen] = useState(false);
  const [pendingFieldKey, setPendingFieldKey] = useState<string>('title');
  const [fieldFilter, setFieldFilter] = useState('');
  const [previewMeetingScale, setPreviewMeetingScale] = useState(false);
  const [dragSlideFrom, setDragSlideFrom] = useState<number | null>(null);
  const [dragSlideOver, setDragSlideOver] = useState<number | null>(null);
  const slideListRef = useRef<HTMLDivElement | null>(null);
  const slideDragRef = useRef<{
    from: number;
    pointerId: number;
    over: number | null;
  } | null>(null);
  const slideDragListenersRef = useRef<{
    move: (event: PointerEvent) => void;
    up: (event: PointerEvent) => void;
  } | null>(null);
  const slidesRef = useRef<ChangeCabDeckSlide[]>(slides);
  slidesRef.current = slides;
  const meetingFontScale = loadCabFontScale();
  const editorFontScale = previewMeetingScale ? meetingFontScale : 1;
  const canvasRef = useRef<HTMLDivElement | null>(null);
  const imageInputRef = useRef<HTMLInputElement | null>(null);
  const imageClickTimerRef = useRef<number | null>(null);
  const dragMoveRafRef = useRef<number | null>(null);
  const pendingDragPtRef = useRef<{ x: number; y: number } | null>(null);
  const dragRef = useRef<{
    mode: DragMode;
    key: string | number;
    startX: number;
    startY: number;
    orig: ChangeCabDeckElement;
  } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const next = isGlobal
        ? await changesApi.getGlobalCabDeck()
        : await changesApi.getChangeCabDeck(changeId);
      setDeck(next);
      setSlides(
        (next.slides?.length
          ? next.slides
          : [{ title: 'Capa da mudança', sort_order: 0, is_active: true, elements: [] }]
        ).map((s) => {
          const { layout } = resolveSlideLayout(s.title, s.layout);
          return layout ? { ...s, layout } : s;
        }),
      );
      setSlideIndex(0);
      setSelectedKey(null);
      if (next.fields?.[0]?.key) setPendingFieldKey(next.fields[0].key);
      /* Pré-aquece full-bleed (seed autenticado ou /cab-cco legado). */
      (next.slides || []).forEach((s) => {
        const bg = (s.layout as { backgroundImage?: string } | null | undefined)?.backgroundImage;
        const elImg = (s.elements || []).find((el) => el.type === 'image')?.content;
        const raw = String(bg || elImg || '');
        const href = rewriteLegacyCabBrandImagePath(raw);
        if (!href.startsWith('/')) return;
        if (href.startsWith('/uploads/')) {
          void fetchAuthenticatedObjectUrl(href)
            .then((blobUrl) => {
              const img = new Image();
              img.decoding = 'async';
              img.onload = () => {
                window.setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
              };
              img.src = blobUrl;
            })
            .catch(() => undefined);
          return;
        }
        if (!href.startsWith('/cab-cco/')) return;
        const img = new Image();
        img.decoding = 'async';
        img.src = href;
      });
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Não foi possível abrir a apresentação.'));
    } finally {
      setLoading(false);
    }
  }, [changeId, isGlobal]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    return () => {
      if (imageClickTimerRef.current != null) {
        window.clearTimeout(imageClickTimerRef.current);
        imageClickTimerRef.current = null;
      }
      if (dragMoveRafRef.current != null) {
        window.cancelAnimationFrame(dragMoveRafRef.current);
        dragMoveRafRef.current = null;
      }
      const slideListeners = slideDragListenersRef.current;
      if (slideListeners) {
        window.removeEventListener('pointermove', slideListeners.move);
        window.removeEventListener('pointerup', slideListeners.up);
        window.removeEventListener('pointercancel', slideListeners.up);
        slideDragListenersRef.current = null;
      }
    };
  }, []);

  const current = slides[slideIndex] || null;
  const { template: currentTemplate, layout: currentLayout } = useMemo(
    () => resolveSlideLayout(current?.title, current?.layout),
    [current?.title, current?.layout],
  );
  const brandBgForHits = useMemo(() => {
    if (!isBrandCabTemplate(currentTemplate)) return null;
    const layout = currentLayout as CabBrandLayout | null;
    return (
      layout?.backgroundImage ||
      current?.elements?.find((el) => el.type === 'image')?.content ||
      null
    );
  }, [currentTemplate, currentLayout, current?.elements]);

  const editableElements = useMemo(() => {
    const els = current?.elements || [];
    if (!brandBgForHits) return els.map((el, i) => ({ el, i }));
    const bg = rewriteLegacyCabBrandImagePath(String(brandBgForHits));
    return els
      .map((el, i) => ({ el, i }))
      .filter(({ el }) => {
        if (el.type !== 'image') return true;
        const src = rewriteLegacyCabBrandImagePath(String(el.content || ''));
        return src !== bg;
      });
  }, [current?.elements, brandBgForHits]);


  const updateCurrentLayout = (next: CabSlideLayout) => {
    setSlides((prev) =>
      prev.map((s, i) =>
        i === slideIndex ? { ...s, layout: next as ChangeCabDeckSlide['layout'] } : s,
      ),
    );
  };
  const selected = useMemo(() => {
    if (!current || selectedKey == null) return null;
    return current.elements.find((el, i) => String(elementKey(el, i)) === String(selectedKey)) || null;
  }, [current, selectedKey]);

  const selectedLayerInfo = useMemo(() => {
    if (!current || selectedKey == null) return null;
    const els = current.elements || [];
    const layer = elementLayerIndex(els, selectedKey);
    const total = sortCabDeckElements(els).length;
    if (layer == null || total === 0) return null;
    return { layer, total };
  }, [current, selectedKey]);

  const catalogFields = deck?.fields || [];
  const filteredFields = useMemo(() => {
    const q = fieldFilter.trim().toLowerCase();
    if (!q) return catalogFields;
    return catalogFields.filter(
      (f) => f.label.toLowerCase().includes(q) || f.key.toLowerCase().includes(q),
    );
  }, [catalogFields, fieldFilter]);

  const previewFields = useMemo(() => {
    const map: Record<string, string> = {};
    catalogFields.forEach((f) => {
      const real = deck?.preview_values?.[f.key];
      map[f.key] = real && real.trim() ? real : `[${f.label}]`;
    });
    return map;
  }, [catalogFields, deck?.preview_values]);

  const updateCurrentElements = (updater: (els: ChangeCabDeckElement[]) => ChangeCabDeckElement[]) => {
    setSlides((prev) => {
      const next = prev.map((slide, index) =>
        index === slideIndex ? { ...slide, elements: updater(slide.elements || []) } : slide,
      );
      slidesRef.current = next;
      return next;
    });
  };

  const addElement = (type: ChangeCabDeckElementType, fieldKey?: string) => {
    const clientKey = newTempId();
    const base: ChangeCabDeckElement = {
      clientKey,
      sort_order: (current?.elements.length || 0) + 1,
      type,
      content: type === 'label' ? 'Objetivo:' : type === 'text' ? 'Texto livre' : '',
      field_key: type === 'field' ? fieldKey || catalogFields[0]?.key || 'title' : null,
      style: {
        fontWeight: type === 'label' ? 700 : 400,
        fontSizeVw: type === 'label' ? 1.3 : 1.1,
        color: type === 'label' ? '#25408D' : '#1a1a1a',
        objectFit: 'contain',
        crop: type === 'image' ? { ...DEFAULT_CROP } : undefined,
      },
      x_pct: 32,
      y_pct: 20 + ((current?.elements.length || 0) % 4) * 14,
      w_pct: type === 'image' ? 30 : 55,
      h_pct: type === 'image' ? 28 : type === 'label' ? 6 : 18,
    };
    updateCurrentElements((els) => [...els, base]);
    setSelectedKey(clientKey);
    if (type === 'image') {
      if (imageClickTimerRef.current != null) window.clearTimeout(imageClickTimerRef.current);
      imageClickTimerRef.current = window.setTimeout(() => {
        imageClickTimerRef.current = null;
        imageInputRef.current?.click();
      }, 120);
    }
  };

  const confirmAddField = () => {
    if (!pendingFieldKey) return;
    addElement('field', pendingFieldKey);
    setFieldPickerOpen(false);
    setFieldFilter('');
  };

  const applyToSelected = (patch: Partial<ChangeCabDeckElement>) => {
    if (!current || selectedKey == null) return;
    applyToElement(selectedKey, patch);
  };

  const applyToElement = (key: string | number, patch: Partial<ChangeCabDeckElement>) => {
    updateCurrentElements((els) =>
      els.map((el, i) => {
        if (String(elementKey(el, i)) !== String(key)) return el;
        /* Evita re-render inútil (ex.: commit Quill com o mesmo HTML). */
        if (
          patch.content !== undefined &&
          Object.keys(patch).length === 1 &&
          el.content === patch.content
        ) {
          return el;
        }
        return {
          ...el,
          ...patch,
          style: { ...(el.style || {}), ...((patch.style as object) || {}) },
        };
      }),
    );
  };

  const removeSelected = () => {
    if (selectedKey == null) return;
    updateCurrentElements((els) =>
      els.filter((el, i) => String(elementKey(el, i)) !== String(selectedKey)),
    );
    setSelectedKey(null);
  };

  const moveSelectedLayer = (direction: CabDeckLayerDirection) => {
    if (!canManage || selectedKey == null) return;
    updateCurrentElements((els) => moveElementLayer(els, selectedKey, direction));
  };

  const onSelectSlide = (index: number) => {
    setSlideIndex(index);
    setSelectedKey(null);
  };

  const detachSlideDragListeners = () => {
    const listeners = slideDragListenersRef.current;
    if (!listeners) return;
    window.removeEventListener('pointermove', listeners.move);
    window.removeEventListener('pointerup', listeners.up);
    window.removeEventListener('pointercancel', listeners.up);
    slideDragListenersRef.current = null;
  };

  const insertSlide = (at: number, kind: CabNewSlideKind = 'livre') => {
    const built = buildNewCabDeckSlide(kind, at);
    const next: ChangeCabDeckSlide = {
      title: built.title,
      sort_order: built.sort_order,
      is_active: built.is_active,
      elements: [],
      layout: built.layout as ChangeCabDeckSlide['layout'],
    };
    setSlides((prev) => {
      const copy = [...prev];
      copy.splice(at, 0, next);
      return copy.map((s, i) => ({ ...s, sort_order: i }));
    });
    setSlideIndex(at);
    setSelectedKey(null);
    setNewSlidePickerOpen(false);
    if (kind === 'imagem') {
      setInfo('Slide de imagem criado. Use “Trocar imagem…” para enviar a arte (até 2 MB).');
    }
  };

  const reorderSlides = (from: number, to: number) => {
    if (from === to || from < 0 || to < 0 || from >= slides.length || to >= slides.length) return;
    setSlides((prev) => {
      const copy = [...prev];
      const [moved] = copy.splice(from, 1);
      copy.splice(to, 0, moved);
      return copy.map((s, i) => ({ ...s, sort_order: i }));
    });
    setSlideIndex((prev) => {
      if (prev === from) return to;
      if (from < prev && to >= prev) return prev - 1;
      if (from > prev && to <= prev) return prev + 1;
      return prev;
    });
    setSelectedKey(null);
  };

  const indexFromPointerY = (clientY: number): number | null => {
    const root = slideListRef.current;
    if (!root) return null;
    const items = Array.from(root.querySelectorAll<HTMLElement>('[data-slide-index]'));
    if (!items.length) return null;
    for (const node of items) {
      const box = node.getBoundingClientRect();
      const mid = box.top + box.height / 2;
      if (clientY < mid) {
        const idx = Number(node.dataset.slideIndex);
        return Number.isNaN(idx) ? null : idx;
      }
    }
    return items.length - 1;
  };

  const onSlideDragHandlePointerDown = (event: React.PointerEvent, index: number) => {
    if (!canManage) return;
    event.preventDefault();
    event.stopPropagation();
    detachSlideDragListeners();
    slideDragRef.current = { from: index, pointerId: event.pointerId, over: index };
    setDragSlideFrom(index);
    setDragSlideOver(index);

    const onMove = (ev: PointerEvent) => {
      const drag = slideDragRef.current;
      if (!drag || ev.pointerId !== drag.pointerId) return;
      const over = indexFromPointerY(ev.clientY);
      if (over != null) {
        drag.over = over;
        setDragSlideOver((prev) => (prev === over ? prev : over));
      }
    };
    const onUp = (ev: PointerEvent) => {
      const drag = slideDragRef.current;
      detachSlideDragListeners();
      if (!drag || ev.pointerId !== drag.pointerId) {
        slideDragRef.current = null;
        setDragSlideFrom(null);
        setDragSlideOver(null);
        return;
      }
      const to = drag.over ?? indexFromPointerY(ev.clientY);
      slideDragRef.current = null;
      setDragSlideFrom(null);
      setDragSlideOver(null);
      if (to != null) reorderSlides(drag.from, to);
    };
    slideDragListenersRef.current = { move: onMove, up: onUp };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  };

  const deleteSlide = async (index: number) => {
    if (slides.length <= 1) {
      setError('Mantenha ao menos um slide.');
      return;
    }
    const ok = await showConfirm({
      title: 'Excluir slide',
      message: `Excluir “${slides[index]?.title}”?`,
      confirmLabel: 'Excluir',
      cancelLabel: 'Cancelar',
    });
    if (!ok) return;
    setSlides((prev) => prev.filter((_, i) => i !== index).map((s, i) => ({ ...s, sort_order: i })));
    setSlideIndex((value) => Math.max(0, Math.min(value, slides.length - 2)));
    setSelectedKey(null);
  };

  const toggleSlideActive = (index: number) => {
    const target = slides[index];
    if (!target) return;
    const currentlyActive = target.is_active !== false;
    if (currentlyActive) {
      const activeCount = slides.filter((s) => s.is_active !== false).length;
      if (activeCount <= 1) {
        setError('Mantenha ao menos um slide ativo.');
        return;
      }
    }
    setSlides((prev) =>
      prev.map((s, i) => (i === index ? { ...s, is_active: !currentlyActive } : s)),
    );
    setInfo(
      currentlyActive
        ? 'Slide desativado — não aparece na reunião. Salve para gravar.'
        : 'Slide ativado. Salve para gravar.',
    );
  };

  const onPointerDown = (event: React.PointerEvent, key: string | number, mode: DragMode) => {
    if (!canManage || !current) return;
    const el = current.elements.find((item, i) => String(elementKey(item, i)) === String(key));
    if (!el) return;
    event.preventDefault();
    event.stopPropagation();
    setSelectedKey(key);
    dragRef.current = {
      mode,
      key,
      startX: event.clientX,
      startY: event.clientY,
      orig: { ...el },
    };
    (event.target as HTMLElement).setPointerCapture?.(event.pointerId);
  };

  const applyDragPoint = (clientX: number, clientY: number) => {
    const drag = dragRef.current;
    const box = canvasRef.current?.getBoundingClientRect();
    if (!drag || !box || !drag.mode) return;
    const dx = ((clientX - drag.startX) / box.width) * 100;
    const dy = ((clientY - drag.startY) / box.height) * 100;
    updateCurrentElements((els) =>
      els.map((el, i) => {
        if (String(elementKey(el, i)) !== String(drag.key)) return el;
        if (drag.mode === 'move') {
          return {
            ...el,
            x_pct: clamp(drag.orig.x_pct + dx, 0, 100 - el.w_pct),
            y_pct: clamp(drag.orig.y_pct + dy, 0, 100 - el.h_pct),
          };
        }
        return {
          ...el,
          w_pct: clamp(drag.orig.w_pct + dx, 8, 100 - el.x_pct),
          h_pct: clamp(drag.orig.h_pct + dy, 4, 100 - el.y_pct),
        };
      }),
    );
  };

  const onPointerMove = (event: React.PointerEvent) => {
    if (!dragRef.current?.mode) return;
    pendingDragPtRef.current = { x: event.clientX, y: event.clientY };
    if (dragMoveRafRef.current != null) return;
    dragMoveRafRef.current = window.requestAnimationFrame(() => {
      dragMoveRafRef.current = null;
      const pt = pendingDragPtRef.current;
      if (!pt) return;
      applyDragPoint(pt.x, pt.y);
    });
  };

  const onPointerUp = () => {
    if (dragMoveRafRef.current != null) {
      window.cancelAnimationFrame(dragMoveRafRef.current);
      dragMoveRafRef.current = null;
    }
    const pt = pendingDragPtRef.current;
    if (pt && dragRef.current?.mode) applyDragPoint(pt.x, pt.y);
    pendingDragPtRef.current = null;
    dragRef.current = null;
  };

  const save = async () => {
    if (!deck || !canManage || deck.is_readonly_preview) return;
    window.dispatchEvent(new Event('cab-deck-quill-flush'));
    const slidesSnapshot = slidesRef.current;
    setSaving(true);
    setError(null);
    setInfo(null);
    try {
      const payloadSlides = slidesSnapshot.map((slide, index) => ({
        ...slide,
        sort_order: index,
        elements: (slide.elements || []).map((el, elIndex) => {
          const { clientKey: _clientKey, ...rest } = el;
          const isRich = el.type === 'label' || el.type === 'text';
          return {
            ...rest,
            content: isRich ? sanitizeCabDeckHtml(String(rest.content || '')) : rest.content,
            id: typeof el.id === 'number' ? el.id : undefined,
            sort_order: elIndex,
          };
        }),
      }));
      const saved = await changesApi.saveCabDeck(deck.id, { theme: deck.theme, slides: payloadSlides });
      setDeck(saved);
      setSlides(saved.slides);
      slidesRef.current = saved.slides;
      setInfo('Apresentação salva.');
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Não foi possível salvar a apresentação.'));
    } finally {
      setSaving(false);
    }
  };

  const uploadImage = async (file: File) => {
    const localError = validateCabDeckImageFile(file);
    if (localError) {
      setError(localError);
      return;
    }
    try {
      const { url } = await changesApi.uploadCabDeckImage(
        file,
        isGlobal ? undefined : Number.isFinite(changeId) ? changeId : undefined,
      );
      if (isBrandCabTemplate(currentTemplate)) {
        updateCurrentLayout({
          template: currentTemplate,
          backgroundImage: url,
        });
        updateCurrentElements((els) => {
          const idx = els.findIndex((el) => el.type === 'image');
          if (idx >= 0) {
            return els.map((el, i) => (i === idx ? { ...el, content: url } : el));
          }
          return [
            ...els,
            {
              clientKey: newTempId(),
              type: 'image' as const,
              content: url,
              sort_order: els.length,
              x_pct: 0,
              y_pct: 0,
              w_pct: 100,
              h_pct: 100,
              style: { objectFit: 'cover' as const },
            },
          ];
        });
        setInfo('Imagem do slide de marca atualizada.');
        return;
      }
      applyToSelected({ type: 'image', content: url });
      setInfo('Imagem adicionada ao bloco selecionado.');
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Falha no upload da imagem.'));
    }
  };

  const selectedFieldPreview =
    selected?.type === 'field' && selected.field_key
      ? previewFields[selected.field_key] || '—'
      : null;

  if (loading) {
    return (
      <div className="container-fluid py-4">
        <Spinner animation="border" size="sm" className="me-2" /> Carregando apresentação…
      </div>
    );
  }

  return (
    <div className="container-fluid py-3 cab-deck-editor">
      <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
        <div>
          <Link
            to={isGlobal ? '/changes/cadastros/cab-slides' : `/changes/${changeId}`}
            className="btn btn-sm btn-outline-secondary mb-2"
          >
            Voltar
          </Link>
          <h2 className="h4 mb-0">
            {isGlobal ? 'Editor do modelo global CAB' : `Apresentação da mudança #${changeId}`}
          </h2>
          <p className="text-muted small mb-0">
            {isGlobal ? (
              <>
                Modelo padrão clonado para cada mudança. <strong>Desativar</strong> esconde o slide
                nas reuniões sem apagar. Alterações aqui não mudam apresentações locais já
                existentes.
              </>
            ) : (
              <>
                Apresentação <strong>local desta mudança</strong> — o que editar aqui não altera o
                modelo global nem outras mudanças.
              </>
            )}
          </p>
        </div>
        <div className="d-flex gap-2">
          {canManage ? (
            <Button onClick={() => void save()} disabled={saving}>
              {saving ? 'Salvando…' : 'Salvar'}
            </Button>
          ) : null}
        </div>
      </div>

      {error ? <Alert variant="danger">{error}</Alert> : null}
      {info ? (
        <Alert variant="success" onClose={() => setInfo(null)} dismissible>
          {info}
        </Alert>
      ) : null}
      {Math.abs(meetingFontScale - 1) > 0.001 ? (
        <Alert variant="info" className="py-2">
          Na reunião a escala de leitura está em{' '}
          <strong>{Math.round(meetingFontScale * 100)}%</strong> (A+/A−). O editor mostra o tamanho
          real do deck em <strong>100%</strong>. Para comparar com a reunião, marque a prévia abaixo
          ou use Ctrl+0 na reunião.
        </Alert>
      ) : null}

      <input
        ref={imageInputRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg,image/webp"
        className="d-none"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void uploadImage(file);
          e.target.value = '';
        }}
      />

      <Row className="g-3">
        <Col lg={2}>
          <CabDeckSlideList
            slides={slides}
            slideIndex={slideIndex}
            canManage={canManage}
            dragSlideFrom={dragSlideFrom}
            dragSlideOver={dragSlideOver}
            listRef={slideListRef}
            onInsert={() => setNewSlidePickerOpen(true)}
            onToggleActive={toggleSlideActive}
            onDelete={(index) => void deleteSlide(index)}
            onSelectSlide={onSelectSlide}
            onDragHandlePointerDown={onSlideDragHandlePointerDown}
          />
        </Col>

        <Col lg={7}>
          {canManage ? (
            <div className="d-flex flex-wrap gap-2 mb-2">
              <Button size="sm" variant="outline-primary" onClick={() => addElement('label')}>
                + Rótulo (texto fixo)
              </Button>
              <Button
                size="sm"
                variant="outline-primary"
                onClick={() => {
                  setPendingFieldKey(catalogFields[0]?.key || 'title');
                  setFieldPickerOpen(true);
                }}
              >
                + Campo da mudança…
              </Button>
              <Button size="sm" variant="outline-primary" onClick={() => addElement('text')}>
                + Texto livre
              </Button>
              <Button size="sm" variant="outline-primary" onClick={() => addElement('image')}>
                + Imagem
              </Button>
            </div>
          ) : null}
          <div
            ref={canvasRef}
            className="cab-deck-editor-stage"
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerLeave={onPointerUp}
          >
            <CabDeckSlideCanvas
              slide={current}
              theme={deck?.theme || ({} as ChangeCabDeck['theme'])}
              fields={previewFields}
              selectedId={selectedKey}
              onSelect={setSelectedKey}
              interactive={canManage}
              fontScale={editorFontScale}
              onStarPlace={
                canManage && currentTemplate === 'conclusao'
                  ? (phaseIndex, dateIndex) => {
                      const layout = currentLayout as CabConclusaoLayout;
                      updateCurrentLayout({
                        ...layout,
                        star_phase_index: phaseIndex,
                        star_date_index: dateIndex,
                      });
                    }
                  : undefined
              }
            />
            {canManage && current
              ? editableElements.map(({ el, i }) => {
                  const key = elementKey(el, i);
                  return (
                    <React.Fragment key={`h-${key}`}>
                      <div
                        className={`cab-deck-hit${String(selectedKey) === String(key) ? ' is-selected' : ''}`}
                        style={{
                          left: `${el.x_pct}%`,
                          top: `${el.y_pct}%`,
                          width: `${el.w_pct}%`,
                          height: `${el.h_pct}%`,
                        }}
                        onPointerDown={(e) => onPointerDown(e, key, 'move')}
                      />
                      {String(selectedKey) === String(key) ? (
                        <div
                          className="cab-deck-resize"
                          style={{
                            left: `calc(${el.x_pct}% + ${el.w_pct}% - 10px)`,
                            top: `calc(${el.y_pct}% + ${el.h_pct}% - 10px)`,
                          }}
                          onPointerDown={(e) => onPointerDown(e, key, 'resize')}
                        />
                      ) : null}
                    </React.Fragment>
                  );
                })
              : null}
          </div>
          {!isGlobal ? (
            <p className="small text-muted mt-2 mb-0">
              A prévia usa os valores reais desta mudança. No modelo global, campos aparecem como
              [Nome do campo].
            </p>
          ) : (
            <p className="small text-muted mt-2 mb-0">
              Modelo global: campos mostram o marcador [rótulo]. Na reunião, o valor vem da mudança
              da pauta.
            </p>
          )}
          <Form.Check
            className="mt-2"
            type="switch"
            id="cab-preview-meeting-scale"
            label={`Prévia com escala da reunião (${Math.round(meetingFontScale * 100)}%)`}
            checked={previewMeetingScale}
            onChange={(e) => setPreviewMeetingScale(e.target.checked)}
          />
          <p className="small text-muted mb-0">
            Desligado = tamanho real do deck (100%), igual à reunião com Ctrl+0. Ligado = mesmo
            zoom A+/A− da última apresentação nesta sessão.
          </p>
        </Col>

        <Col lg={3}>
          <div className="border rounded p-3 bg-white">
            <h3 className="h6">Slide</h3>
            <Form.Group className="mb-3">
              <Form.Label className="small">Título do slide</Form.Label>
              <Form.Control
                value={current?.title || ''}
                disabled={!canManage}
                onChange={(e) =>
                  setSlides((prev) =>
                    prev.map((s, i) => (i === slideIndex ? { ...s, title: e.target.value } : s)),
                  )
                }
              />
              <Form.Text>
                Use “capa”, “Riscos e Impactos”, “Conclusão”, “Fechamento Petronect”, “Elenco
                Petronect” ou “Somos GPTW” no título para layouts especiais. Outro nome = freeform.
              </Form.Text>
            </Form.Group>

            {currentTemplate === 'riscos_impactos' && currentLayout && 'rows' in currentLayout ? (
              <CabDeckRiscosEditor
                layout={currentLayout as CabRiscosLayout}
                canManage={canManage}
                onChange={updateCurrentLayout}
              />
            ) : null}

            {currentTemplate === 'conclusao' && currentLayout && 'dates' in currentLayout ? (
              <CabDeckConclusaoEditor
                layout={currentLayout as CabConclusaoLayout}
                canManage={canManage}
                onChange={updateCurrentLayout}
              />
            ) : null}

            {isBrandCabTemplate(currentTemplate) ? (
              <CabDeckBrandEditor
                canManage={canManage}
                currentImage={(currentLayout as CabBrandLayout | null)?.backgroundImage}
                onPickImage={() => imageInputRef.current?.click()}
              />
            ) : null}

            <h3 className="h6">Elemento selecionado</h3>
            {!selected ? (
              <p className="small text-muted mb-0">
                {isStructuredCabTemplate(currentTemplate)
                  ? 'Use os botões acima para adicionar rótulo/campo/texto/imagem por cima do template, ou clique em um bloco no canvas.'
                  : 'Clique em um bloco no canvas para editar.'}
              </p>
            ) : (
              <>
                <Form.Group className="mb-2">
                  <Form.Label className="small">Tipo</Form.Label>
                  <Form.Select
                    value={selected.type}
                    disabled={!canManage}
                    onChange={(e) =>
                      applyToSelected({ type: e.target.value as ChangeCabDeckElementType })
                    }
                  >
                    <option value="label">Rótulo (texto fixo)</option>
                    <option value="field">Campo da mudança</option>
                    <option value="text">Texto livre</option>
                    <option value="image">Imagem</option>
                  </Form.Select>
                </Form.Group>
                {selectedLayerInfo && canManage ? (
                  <Form.Group className="mb-2">
                    <Form.Label className="small">
                      Camada (frente / trás): {selectedLayerInfo.layer} de {selectedLayerInfo.total}
                    </Form.Label>
                    <div className="d-flex flex-wrap gap-1">
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        disabled={selectedLayerInfo.layer <= 1}
                        onClick={() => moveSelectedLayer('back')}
                        title="Enviar para o fundo"
                      >
                        Fundo
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        disabled={selectedLayerInfo.layer <= 1}
                        onClick={() => moveSelectedLayer('backward')}
                        title="Uma camada para trás"
                      >
                        Atrás
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        disabled={selectedLayerInfo.layer >= selectedLayerInfo.total}
                        onClick={() => moveSelectedLayer('forward')}
                        title="Uma camada para frente"
                      >
                        Frente
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        disabled={selectedLayerInfo.layer >= selectedLayerInfo.total}
                        onClick={() => moveSelectedLayer('front')}
                        title="Trazer para o topo"
                      >
                        Topo
                      </Button>
                    </div>
                    <Form.Text className="d-block">
                      Elementos com camada maior aparecem por cima no slide (textos, imagens e campos).
                    </Form.Text>
                  </Form.Group>
                ) : null}
                {selected.type === 'field' ? (
                  <Form.Group className="mb-2">
                    <Form.Label className="small">Qual campo da ficha?</Form.Label>
                    <Form.Select
                      value={selected.field_key || ''}
                      disabled={!canManage}
                      onChange={(e) => applyToSelected({ field_key: e.target.value })}
                    >
                      {catalogFields.map((f) => (
                        <option key={f.key} value={f.key}>
                          {f.label}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Text className="d-block mt-1">
                      Prévia: <strong>{selectedFieldPreview}</strong>
                    </Form.Text>
                  </Form.Group>
                ) : selected.type === 'image' ? (
                  <Form.Group className="mb-2">
                    <Form.Label className="small">Arquivo da imagem</Form.Label>
                    <div className="d-flex flex-wrap gap-1 mb-1">
                      <Button
                        size="sm"
                        variant="outline-primary"
                        disabled={!canManage}
                        onClick={() => imageInputRef.current?.click()}
                      >
                        Escolher arquivo…
                      </Button>
                      {selected.content ? (
                        <Button
                          size="sm"
                          variant="outline-danger"
                          disabled={!canManage}
                          onClick={() => {
                            applyToSelected({ content: '' });
                            if (imageInputRef.current) imageInputRef.current.value = '';
                            setInfo('Imagem removida do bloco. Salve para gravar.');
                          }}
                        >
                          Remover imagem
                        </Button>
                      ) : null}
                    </div>
                    <Form.Text>
                      PNG/JPEG/WebP até 2 MB. Remover limpa o arquivo do bloco (o retângulo
                      permanece). Sem arquivo o bloco fica vazio no slide.
                    </Form.Text>
                    <Form.Label className="small mt-2">Encaixe</Form.Label>
                    <Form.Select
                      value={selected.style?.objectFit || 'contain'}
                      disabled={!canManage}
                      onChange={(e) =>
                        applyToSelected({
                          style: {
                            ...(selected.style || {}),
                            objectFit: e.target.value as 'contain' | 'cover' | 'fill',
                          },
                        })
                      }
                    >
                      <option value="contain">Conter (sem cortar)</option>
                      <option value="cover">Cobrir (pode cortar)</option>
                      <option value="fill">Esticar</option>
                    </Form.Select>
                    <Form.Label className="small mt-2">Recorte retangular (% da imagem)</Form.Label>
                    {(
                      [
                        ['xPct', 'Esquerda'],
                        ['yPct', 'Topo'],
                        ['wPct', 'Largura'],
                        ['hPct', 'Altura'],
                      ] as const
                    ).map(([field, label]) => {
                      const crop = { ...DEFAULT_CROP, ...(selected.style?.crop || {}) };
                      return (
                        <div key={field} className="mb-1">
                          <Form.Label className="small mb-0">
                            {label}: {crop[field]}%
                          </Form.Label>
                          <Form.Range
                            min={field === 'wPct' || field === 'hPct' ? 5 : 0}
                            max={100}
                            step={1}
                            value={crop[field]}
                            disabled={!canManage}
                            onChange={(e) => {
                              const next = {
                                ...crop,
                                [field]: Number(e.target.value),
                              };
                              if (next.xPct + next.wPct > 100) next.wPct = 100 - next.xPct;
                              if (next.yPct + next.hPct > 100) next.hPct = 100 - next.yPct;
                              applyToSelected({
                                style: { ...(selected.style || {}), crop: next },
                              });
                            }}
                          />
                        </div>
                      );
                    })}
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      className="mt-1"
                      disabled={!canManage}
                      onClick={() =>
                        applyToSelected({
                          style: { ...(selected.style || {}), crop: { ...DEFAULT_CROP } },
                        })
                      }
                    >
                      Limpar recorte
                    </Button>
                  </Form.Group>
                ) : (
                  <Form.Group className="mb-2">
                    <Form.Label className="small">Texto</Form.Label>
                    <CabDeckQuillEditor
                      elementKey={selectedKey ?? 'none'}
                      content={selected.content || ''}
                      disabled={!canManage}
                      onCommit={(html, key) => applyToElement(key, { content: html })}
                    />
                    <Form.Text>
                      Selecione só a palavra/trecho e use a barra acima (B / I / U / tamanho / cor A).
                      Não use seletor de cor do bloco — isso pintaria tudo. A prévia no slide atualiza
                      em seguida (ou ao clicar fora). O slider abaixo é só o tamanho-base do bloco.
                    </Form.Text>
                  </Form.Group>
                )}
                {selected.type === 'field' ? (
                  <>
                    <Form.Text className="d-block mb-2">
                      O retângulo azul limita o valor do campo. Amplie pelo canto se precisar de mais
                      linhas. Cor e tamanho-base valem para o bloco inteiro do campo.
                    </Form.Text>
                    <Form.Group className="mb-2">
                      <Form.Label className="small">Cor do campo</Form.Label>
                      <Form.Control
                        type="color"
                        value={selected.style?.color || '#25408D'}
                        disabled={!canManage}
                        onChange={(e) =>
                          applyToSelected({
                            style: { ...(selected.style || {}), color: e.target.value },
                          })
                        }
                      />
                    </Form.Group>
                  </>
                ) : null}
                <Form.Group className="mb-2">
                  <Form.Label className="small">
                    Tamanho-base (% do slide): {selected.style?.fontSizeVw ?? 1.1}
                  </Form.Label>
                  <Form.Range
                    min={0.7}
                    max={4}
                    step={0.1}
                    value={selected.style?.fontSizeVw ?? 1.1}
                    disabled={!canManage}
                    onChange={(e) =>
                      applyToSelected({
                        style: { ...(selected.style || {}), fontSizeVw: Number(e.target.value) },
                      })
                    }
                  />
                </Form.Group>
                {canManage ? (
                  <Button size="sm" variant="outline-danger" onClick={removeSelected}>
                    Remover elemento
                  </Button>
                ) : null}
              </>
            )}
          </div>
          {!isGlobal && canManage ? (
            <Button
              className="mt-2"
              size="sm"
              variant="outline-primary"
              onClick={async () => {
                const ok = await showConfirm({
                  title: 'Trazer slides do modelo global',
                  message:
                    'Inclui no final desta apresentação os slides do modelo global que ainda não existem aqui (por título). Não apaga nem altera os slides que você já tem. Continuar?',
                  confirmLabel: 'Trazer slides',
                  cancelLabel: 'Cancelar',
                });
                if (!ok) return;
                setSaving(true);
                setError(null);
                try {
                  const next = await changesApi.appendCabDeckFromGlobal(changeId);
                  setDeck(next);
                  setSlides(next.slides || []);
                  setInfo(
                    `Slides atualizados (${next.slides?.length || 0} no total). Revise e salve se quiser ajustar.`,
                  );
                } catch (err: unknown) {
                  setError(
                    getApiErrorMessage(err, 'Não foi possível trazer os slides do modelo global.'),
                  );
                } finally {
                  setSaving(false);
                }
              }}
            >
              Trazer slides do modelo global
            </Button>
          ) : null}
          {isGlobal ? (
            <Button
              className="mt-2"
              size="sm"
              variant="outline-secondary"
              onClick={() => navigate('/changes/cadastros/cab-slides')}
            >
              Voltar aos cadastros
            </Button>
          ) : null}
        </Col>
      </Row>

      <Modal show={newSlidePickerOpen} onHide={() => setNewSlidePickerOpen(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Novo slide</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="small text-muted mb-3">
            Escolha o tipo. Você pode misturar tipos na mesma apresentação e renomear o título
            depois sem perder o layout.
          </p>
          <div className="d-grid gap-2">
            {CAB_NEW_SLIDE_OPTIONS.map((option) => (
              <Button
                key={option.kind}
                variant="outline-primary"
                className="text-start"
                onClick={() => insertSlide(slides.length, option.kind)}
              >
                <span className="fw-semibold d-block">{option.label}</span>
                <span className="small text-muted d-block text-wrap">{option.description}</span>
              </Button>
            ))}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setNewSlidePickerOpen(false)}>
            Cancelar
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={fieldPickerOpen} onHide={() => setFieldPickerOpen(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Escolher campo da mudança</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="small text-muted">
            O bloco mostra o valor desse campo na reunião (título, descrição, Go Live, etc.). Só
            entram campos do catálogo oficial.
          </p>
          <Form.Control
            className="mb-2"
            placeholder="Filtrar por nome…"
            value={fieldFilter}
            onChange={(e) => setFieldFilter(e.target.value)}
            autoFocus
          />
          <div className="list-group" style={{ maxHeight: 320, overflowY: 'auto' }}>
            {filteredFields.map((f) => {
              const preview = previewFields[f.key] || `[${f.label}]`;
              const active = pendingFieldKey === f.key;
              return (
                <button
                  key={f.key}
                  type="button"
                  className={`list-group-item list-group-item-action${active ? ' active' : ''}`}
                  onClick={() => setPendingFieldKey(f.key)}
                  onDoubleClick={() => {
                    setPendingFieldKey(f.key);
                    addElement('field', f.key);
                    setFieldPickerOpen(false);
                    setFieldFilter('');
                  }}
                >
                  <div className="fw-semibold">{f.label}</div>
                  <div className={`small${active ? '' : ' text-muted'}`}>Prévia: {preview}</div>
                </button>
              );
            })}
            {!filteredFields.length ? (
              <div className="list-group-item text-muted small">Nenhum campo encontrado.</div>
            ) : null}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setFieldPickerOpen(false)}>
            Cancelar
          </Button>
          <Button onClick={confirmAddField} disabled={!pendingFieldKey}>
            Inserir campo
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ChangeCabDeckEditorPage;
